import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import api from "../api/axios";
import FormViewSidebar from "../components/FormViewSidebar";
import TablePagination from "../components/TablePagination";
import "../assets/styles/page.css";

import {
  TbEdit,
  TbEyeSearch,
  TbForms,
  TbPlus,
  TbSearch,
  TbTrash,
  TbX,
} from "react-icons/tb";

const PAGE_NAME = "Forms";
const VISIBLE_FIELD_CHIPS = 3;

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const Forms = () => {
  const navigate = useNavigate();

  const [forms, setForms] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewSidebarOpen, setViewSidebarOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  const hasFilters = Boolean(
    debouncedSearch || status || selectedSubCategoryId
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchMeta = async () => {
    try {
      const res = await api.get("/admin/products/sub-categories", {
        headers: getPermissionHeaders("view"),
      });
      setSubCategories(res.data?.data || []);
    } catch (error) {
      showRequestError(error, "Failed to fetch sub-categories.");
    }
  };

  const fetchForms = async (page = 1) => {
    try {
      setLoading(true);

      const res = await api.get("/admin/forms", {
        headers: getPermissionHeaders("view"),
        params: {
          page,
          limit: pageSize,
          search: debouncedSearch || undefined,
          status: status || undefined,
          sub_category_id: selectedSubCategoryId || undefined,
        },
      });

      const nextPagination = res.data?.pagination || {
        page,
        total: 0,
        totalPages: 1,
      };

      // Deleting the last row on a page leaves it empty — step back.
      if (
        (res.data?.data || []).length === 0 &&
        page > 1 &&
        nextPagination.totalPages < page
      ) {
        await fetchForms(Math.max(nextPagination.totalPages, 1));
        return;
      }

      setForms(res.data?.data || []);
      setPagination(nextPagination);
    } catch (error) {
      showRequestError(error, "Failed to fetch forms.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    fetchForms(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, status, selectedSubCategoryId, pageSize]);

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatus("");
    setSelectedSubCategoryId("");
  };

  const openViewForm = async (form) => {
    try {
      const res = await api.get(`/admin/forms/${form.form_id}`, {
        headers: getPermissionHeaders("view"),
      });
      setSelectedForm(res.data?.data || form);
      setViewSidebarOpen(true);
    } catch (error) {
      showRequestError(error, "Failed to load form.");
    }
  };

  const updateFormStatus = (form, newStatus) => {
    if (newStatus === form.status) return;

    confirmDialog({
      message:
        newStatus === "active"
          ? `Activate "${form.title}"? Customers ordering from its sub-category will be asked to fill it in.`
          : `Deactivate "${form.title}"? Customers will no longer see this form.`,
      header: newStatus === "active" ? "Activate form" : "Deactivate form",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          setStatusUpdatingId(form.form_id);

          await api.patch(
            `/admin/forms/${form.form_id}/status`,
            { status: newStatus },
            { headers: getPermissionHeaders("edit") }
          );

          toast.success(`"${form.title}" is now ${newStatus}.`);
          await fetchForms(pagination.page);
        } catch (error) {
          showRequestError(error, "Status update failed.");
        } finally {
          setStatusUpdatingId(null);
        }
      },
    });
  };

  const deleteForm = (form) => {
    confirmDialog({
      message: `Delete "${form.title}"? This cannot be undone.`,
      header: "Delete form",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await api.delete(`/admin/forms/${form.form_id}`, {
            headers: getPermissionHeaders("delete"),
          });
          toast.success("Form deleted successfully.");
          await fetchForms(pagination.page);
        } catch (error) {
          showRequestError(error, "Form delete failed.");
        }
      },
    });
  };

  return (
    <section className="admin-page forms-page">
      <ConfirmDialog />

      <div className="page-header forms-hero-header">
        <div>
          <p>Customisation</p>
          <h1>Forms</h1>
        </div>
        <div className="page-header-actions">
          <div className="page-total-summary">
            <span>Total:</span>
            <strong>{pagination.total || 0}</strong>
          </div>
          <button
            type="button"
            className="primary-action"
            onClick={() => navigate("/forms/add")}
          >
            <TbPlus />
            Add Form
          </button>
        </div>
      </div>

      <div className="page-card dt-card">
        <div className="dt-toolbar">
          <div className="dt-toolbar-title">
            <h2>Form Templates</h2>
            <p>
              Each form collects personalisation details for the
              products in its sub-category.
            </p>
          </div>

          <div className="dt-filters">
            <div
              className="dt-tabs"
              role="tablist"
              aria-label="Filter by status"
            >
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value || "all"}
                  type="button"
                  role="tab"
                  aria-selected={status === tab.value}
                  className={status === tab.value ? "is-active" : ""}
                  onClick={() => setStatus(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <select
              className="dt-select"
              value={selectedSubCategoryId}
              onChange={(event) => setSelectedSubCategoryId(event.target.value)}
              aria-label="Filter by sub-category"
            >
              <option value="">All sub-categories</option>
              {subCategories.map((subCategory) => (
                <option
                  key={subCategory.sub_category_id}
                  value={subCategory.sub_category_id}
                >
                  {subCategory.category?.name
                    ? `${subCategory.category.name} / ${subCategory.name}`
                    : subCategory.name}
                </option>
              ))}
            </select>

            <div className="dt-search">
              <TbSearch />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search forms…"
                aria-label="Search forms"
              />
              {search && (
                <button
                  type="button"
                  className="dt-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <TbX />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="dt-scroll">
          <table className="dt-table forms-table">
            <thead>
              <tr>
                <th>Form</th>
                <th>Linked to</th>
                <th>Fields</th>
                <th>Status</th>
                <th>Last updated</th>
                <th className="dt-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading &&
                forms.length === 0 &&
                Array.from({ length: 5 }, (_, index) => (
                  <tr className="dt-skeleton" key={`skeleton-${index}`}>
                    {[200, 150, 180, 80, 90, 110].map((width, cell) => (
                      <td key={cell}>
                        <span className="dt-skeleton-bar" style={{ width }} />
                      </td>
                    ))}
                  </tr>
                ))}

              {!loading && forms.length === 0 && (
                <tr className="dt-empty">
                  <td colSpan="6">
                    <div className="dt-empty-inner">
                      <span className="dt-empty-icon">
                        <TbForms />
                      </span>

                      {hasFilters ? (
                        <>
                          <strong>No matching forms</strong>
                          <span>Try another search, status or sub-category.</span>
                          <button
                            type="button"
                            className="ghost-action bottonborder"
                            onClick={clearFilters}
                          >
                            Clear filters
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>No forms yet</strong>
                          <span>
                            Create a form and link it to a sub-category to
                            collect names, dates and other details.
                          </span>
                          <button
                            type="button"
                            className="primary-action"
                            onClick={() => navigate("/forms/add")}
                          >
                            <TbPlus />
                            Create your first form
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {forms.map((form) => {
                const fields = form.fields || [];
                const requiredCount = fields.filter(
                  (field) => field.is_required
                ).length;

                return (
                  <tr key={form.form_id}>
                    <td>
                      <div className="dt-entity">
                        <span className="dt-entity-icon">
                          {form.title?.charAt(0)?.toUpperCase() || "F"}
                        </span>

                        <div className="dt-entity-text">
                          <button
                            type="button"
                            className="dt-entity-title"
                            onClick={() => openViewForm(form)}
                            title="Preview form"
                          >
                            {form.title}
                          </button>
                          <span className="dt-entity-sub">
                            {form.description || "No description"}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="dt-stack">
                        <strong>{form.sub_category?.name || "—"}</strong>
                        <span>{form.sub_category?.category?.name || "No category"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="dt-stack" style={{ marginBottom: 6 }}>
                        <strong>
                          {fields.length} {fields.length === 1 ? "field" : "fields"}
                        </strong>
                        <span>{requiredCount} required</span>
                      </div>

                      {fields.length > 0 && (
                        <div className="dt-chips">
                          {fields.slice(0, VISIBLE_FIELD_CHIPS).map((field) => (
                            <span key={field.form_field_id}>{field.field_label}</span>
                          ))}

                          {fields.length > VISIBLE_FIELD_CHIPS && (
                            <span
                              className="dt-chip-more"
                              title={fields
                                .slice(VISIBLE_FIELD_CHIPS)
                                .map((field) => field.field_label)
                                .join(", ")}
                            >
                              +{fields.length - VISIBLE_FIELD_CHIPS} more
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    <td>
                      <span className={`dt-status is-${form.status}`}>
                        <select
                          value={form.status}
                          disabled={statusUpdatingId === form.form_id}
                          aria-label={`Status of ${form.title}`}
                          onChange={(event) =>
                            updateFormStatus(form, event.target.value)
                          }
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </span>
                    </td>

                    <td>
                      <div className="dt-stack">
                        <strong>{formatDate(form.updatedAt || form.createdAt)}</strong>
                        <span>Created {formatDate(form.createdAt)}</span>
                      </div>
                    </td>

                    <td>
                      <div className="dt-actions">
                        <button
                          type="button"
                          title="Preview form"
                          aria-label={`Preview ${form.title}`}
                          onClick={() => openViewForm(form)}
                        >
                          <TbEyeSearch />
                        </button>

                        <button
                          type="button"
                          title="Edit form"
                          aria-label={`Edit ${form.title}`}
                          onClick={() => navigate(`/forms/edit/${form.form_id}`)}
                        >
                          <TbEdit />
                        </button>

                        <button
                          type="button"
                          className="is-danger"
                          title="Delete form"
                          aria-label={`Delete ${form.title}`}
                          onClick={() => deleteForm(form)}
                        >
                          <TbTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagination.total > 0 && (
          <TablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            pageSize={pageSize}
            onPageChange={fetchForms}
            onPageSizeChange={setPageSize}
            disabled={loading}
            itemLabel="forms"
          />
        )}
      </div>

      <FormViewSidebar
        visible={viewSidebarOpen}
        onHide={() => setViewSidebarOpen(false)}
        form={selectedForm}
        onEdit={(form) => {
          setViewSidebarOpen(false);
          navigate(`/forms/edit/${form.form_id}`);
        }}
      />
    </section>
  );
};

export default Forms;
